// src/components/profile/ChangePassword.js
import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import Toast from '../common/Toast';
import api from '../../services/api';

const ChangePassword = () => {
    usePageTitle('Change Password');
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [errors, setErrors] = useState({});

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.current_password) {
            newErrors.current_password = 'Current password is required';
        }

        if (!formData.new_password) {
            newErrors.new_password = 'New password is required';
        } else if (formData.new_password.length < 8) {
            newErrors.new_password = 'Password must be at least 8 characters long';
        }

        if (!formData.confirm_password) {
            newErrors.confirm_password = 'Please confirm your new password';
        } else if (formData.new_password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }

        if (formData.current_password === formData.new_password) {
            newErrors.new_password = 'New password must be different from current password';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('Please fix the errors in the form', 'error');
            return;
        }

        try {
            setLoading(true);
            const response = await api.post('/change-password', {
                current_password: formData.current_password,
                new_password: formData.new_password,
                confirm_password: formData.confirm_password
            });

            const isSuccess =
                response?.data?.status === 'success' ||
                response?.data?.success === true;

            if (isSuccess) {
                showToast(response?.data?.message || 'Password changed successfully!', 'success');
                setFormData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                });
                setErrors({});
            }
        } catch (error) {
            console.error('Change password error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to change password. Please try again.';
            showToast(errorMessage, 'error');

            // Set specific field errors if provided
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, text: '', color: '' };

        let strength = 0;
        if (password.length >= 8) strength++;
        if (password.length >= 12) strength++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^a-zA-Z\d]/.test(password)) strength++;

        const levels = [
            { strength: 0, text: '', color: '' },
            { strength: 1, text: 'Very Weak', color: '#ef4444' },
            { strength: 2, text: 'Weak', color: '#f59e0b' },
            { strength: 3, text: 'Fair', color: '#eab308' },
            { strength: 4, text: 'Good', color: '#10b981' },
            { strength: 5, text: 'Strong', color: '#10b981' }
        ];

        return levels[Math.min(strength, 5)];
    };

    const passwordStrength = getPasswordStrength(formData.new_password);

    return (
        <div className="change-password-container">
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: '' })}
            />

            <div className="change-password-content">
                <div className="change-password-header">
                    <div className="header-icon">
                        <i className="fas fa-key"></i>
                    </div>
                    <h1>Change Password</h1>
                    <p>Update your password to keep your account secure</p>
                </div>

                <div className="change-password-card">
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="current_password">
                                Current Password <span className="required">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.current ? 'text' : 'password'}
                                    id="current_password"
                                    name="current_password"
                                    value={formData.current_password}
                                    onChange={handleChange}
                                    className={errors.current_password ? 'error' : ''}
                                    placeholder="Enter your current password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => togglePasswordVisibility('current')}
                                    tabIndex="-1"
                                >
                                    <i className={`fas fa-eye${showPasswords.current ? '-slash' : ''}`}></i>
                                </button>
                            </div>
                            {errors.current_password && (
                                <span className="error-message">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {errors.current_password}
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="new_password">
                                New Password <span className="required">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.new ? 'text' : 'password'}
                                    id="new_password"
                                    name="new_password"
                                    value={formData.new_password}
                                    onChange={handleChange}
                                    className={errors.new_password ? 'error' : ''}
                                    placeholder="Enter your new password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => togglePasswordVisibility('new')}
                                    tabIndex="-1"
                                >
                                    <i className={`fas fa-eye${showPasswords.new ? '-slash' : ''}`}></i>
                                </button>
                            </div>
                            {formData.new_password && passwordStrength.text && (
                                <div className="password-strength">
                                    <div className="strength-bar">
                                        <div
                                            className="strength-fill"
                                            style={{
                                                width: `${(passwordStrength.strength / 5) * 100}%`,
                                                backgroundColor: passwordStrength.color
                                            }}
                                        ></div>
                                    </div>
                                    <span style={{ color: passwordStrength.color }}>
                                        {passwordStrength.text}
                                    </span>
                                </div>
                            )}
                            {errors.new_password && (
                                <span className="error-message">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {errors.new_password}
                                </span>
                            )}
                            <div className="password-requirements">
                                <p>Password must contain:</p>
                                <ul>
                                    <li className={formData.new_password.length >= 8 ? 'valid' : ''}>
                                        <i className={`fas fa-${formData.new_password.length >= 8 ? 'check' : 'times'}`}></i>
                                        At least 8 characters
                                    </li>
                                    <li className={/[A-Z]/.test(formData.new_password) ? 'valid' : ''}>
                                        <i className={`fas fa-${/[A-Z]/.test(formData.new_password) ? 'check' : 'times'}`}></i>
                                        One uppercase letter
                                    </li>
                                    <li className={/[a-z]/.test(formData.new_password) ? 'valid' : ''}>
                                        <i className={`fas fa-${/[a-z]/.test(formData.new_password) ? 'check' : 'times'}`}></i>
                                        One lowercase letter
                                    </li>
                                    <li className={/\d/.test(formData.new_password) ? 'valid' : ''}>
                                        <i className={`fas fa-${/\d/.test(formData.new_password) ? 'check' : 'times'}`}></i>
                                        One number
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirm_password">
                                Confirm New Password <span className="required">*</span>
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPasswords.confirm ? 'text' : 'password'}
                                    id="confirm_password"
                                    name="confirm_password"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                    className={errors.confirm_password ? 'error' : ''}
                                    placeholder="Confirm your new password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => togglePasswordVisibility('confirm')}
                                    tabIndex="-1"
                                >
                                    <i className={`fas fa-eye${showPasswords.confirm ? '-slash' : ''}`}></i>
                                </button>
                            </div>
                            {errors.confirm_password && (
                                <span className="error-message">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {errors.confirm_password}
                                </span>
                            )}
                            {formData.confirm_password && formData.new_password === formData.confirm_password && (
                                <span className="success-message">
                                    <i className="fas fa-check-circle"></i>
                                    Passwords match
                                </span>
                            )}
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <div className="spinner-small"></div>
                                        Changing Password...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i>
                                        Change Password
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="security-tips">
                    <h3>
                        <i className="fas fa-shield-alt"></i>
                        Security Tips
                    </h3>
                    <ul>
                        <li>
                            <i className="fas fa-check-circle"></i>
                            Use a unique password you don't use anywhere else
                        </li>
                        <li>
                            <i className="fas fa-check-circle"></i>
                            Avoid using personal information in your password
                        </li>
                        <li>
                            <i className="fas fa-check-circle"></i>
                            Change your password regularly
                        </li>
                        <li>
                            <i className="fas fa-check-circle"></i>
                            Never share your password with anyone
                        </li>
                    </ul>
                </div>
            </div>

            <style jsx>{`
                .change-password-container {
                    padding: 2rem;
                }

                .change-password-content {
                    max-width: 700px;
                    margin: 0 auto;
                }

                .change-password-header {
                    text-align: center;
                    margin-bottom: 2rem;
                }

                .header-icon {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                    margin: 0 auto 1rem;
                    color: white;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .change-password-header h1 {
                    font-size: 1.75rem;
                    margin: 0 0 0.5rem 0;
                    font-weight: 700;
                    color: #1f2937;
                }

                .change-password-header p {
                    margin: 0;
                    font-size: 0.95rem;
                    color: #6b7280;
                }

                .change-password-card {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    border: 1px solid #e5e7eb;
                    margin-bottom: 2rem;
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-group label {
                    display: block;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    font-size: 0.95rem;
                }

                .required {
                    color: #ef4444;
                }

                .password-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .password-input-wrapper input {
                    width: 100%;
                    padding: 0.875rem 3rem 0.875rem 1rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 10px;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                }

                .password-input-wrapper input:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .password-input-wrapper input.error {
                    border-color: #ef4444;
                }

                .toggle-password {
                    position: absolute;
                    right: 1rem;
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 0.5rem;
                    transition: color 0.2s ease;
                }

                .toggle-password:hover {
                    color: #667eea;
                }

                .error-message {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #ef4444;
                    font-size: 0.85rem;
                    margin-top: 0.5rem;
                }

                .success-message {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #10b981;
                    font-size: 0.85rem;
                    margin-top: 0.5rem;
                }

                .password-strength {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-top: 0.5rem;
                }

                .strength-bar {
                    flex: 1;
                    height: 6px;
                    background: #e5e7eb;
                    border-radius: 3px;
                    overflow: hidden;
                }

                .strength-fill {
                    height: 100%;
                    transition: all 0.3s ease;
                    border-radius: 3px;
                }

                .password-strength span {
                    font-size: 0.85rem;
                    font-weight: 600;
                    min-width: 80px;
                }

                .password-requirements {
                    margin-top: 1rem;
                    padding: 1rem;
                    background: #f9fafb;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                }

                .password-requirements p {
                    margin: 0 0 0.5rem 0;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #6b7280;
                }

                .password-requirements ul {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 0.5rem;
                }

                .password-requirements li {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.85rem;
                    color: #6b7280;
                }

                .password-requirements li.valid {
                    color: #10b981;
                }

                .password-requirements li i {
                    width: 16px;
                }

                .form-actions {
                    margin-top: 2rem;
                }

                .btn {
                    width: 100%;
                    padding: 1rem;
                    border: none;
                    border-radius: 10px;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
                }

                .btn-primary:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
                }

                .btn-primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .spinner-small {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .security-tips {
                    background: white;
                    border-radius: 16px;
                    padding: 1.5rem;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    border: 1px solid #e5e7eb;
                }

                .security-tips h3 {
                    margin: 0 0 1rem 0;
                    font-size: 1.1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #1f2937;
                    font-weight: 600;
                }

                .security-tips h3 i {
                    color: #3b82f6;
                }

                .security-tips ul {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .security-tips li {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    margin-bottom: 0.75rem;
                    font-size: 0.9rem;
                    line-height: 1.5;
                    color: #4b5563;
                }

                .security-tips li:last-child {
                    margin-bottom: 0;
                }

                .security-tips li i {
                    margin-top: 0.2rem;
                    flex-shrink: 0;
                    color: #10b981;
                }

                @media (max-width: 768px) {
                    .change-password-container {
                        padding: 1rem;
                    }

                    .change-password-card {
                        padding: 1.5rem;
                    }

                    .change-password-header h1 {
                        font-size: 1.5rem;
                    }

                    .header-icon {
                        width: 60px;
                        height: 60px;
                        font-size: 2rem;
                    }

                    .password-requirements ul {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default ChangePassword;
